<template>
  <!-- 
    - Take in user input, send to Algolia using API_Algolia, which returns JSON object: algoliaResults
    - Create a new JSON Object store variable called getSavedTreeNodes
    - Using fullNaics object as a reference, we build up a new, smaller tree including unique naics codes 
      found in algoliaResults, as well as all parents and siblings of those naics codes.
    - Append Example Industries from algoliaResults to the getSavedTreeNodes with the same naics code
    - Pass the getSavedTreeNodes JSON object to TreeView
  -->
  <div>
    <section>
      <label for="search-input" class="keyword-label">{{ !this.mx_isMobile() ? $t('label') : $t('label_mobile') }}</label>
      <b-field role="search" aria-label="Search for naics data with keywords" class="keyword-field">
        <b-input
          id="search-input"
          :placeholder="$t('placeholder')"
          type="search"
          icon-pack="fas"
          icon="search"
          v-model="keyword"
          @input="onSearch"
        >
        </b-input>
      </b-field>
      <div>
        <div v-if="nomatch && this.getSearchKeyword != ''" role="alert" aria-live="assertive" class="noresults">
          {{ $t("no_result") }}
        </div>
        <div v-if="!this.mx_isMobile() && this.getSearchHasResults" role="alert" aria-live="assertive" class="results">
          <b>{{ this.naicsResults.length }}</b> {{ $t('results') }}
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { getConnexion } from "../services/API_Algolia.js";

export default {
  i18n: {
    locale: "en",
    messages: {
      en: {
        no_option: "Sorry, no matching option",
        no_result: "No matching industries found...",
        placeholder: "Keywords...",
        label: "Search for your industry",
        label_mobile: "Search an industry",
        results: "matching industries found"
      },
      fr: {
        no_option: "Désolé, aucunes options correspondantes.",
        no_result: "Aucunes industries correspondantes trouvées...",
        placeholder: "Mots clés...",
        label: "Cherchez pour votre industrie",
        label_mobile: "Chercher une industrie",
        results: "industries correspondantes trouvées"
      }
    }
  }, // end i18n
  props: {
    clearkeyword: {
      type: Boolean,
      default: false
    },
    lang: {
      type: String,
      default: "en"
    }
  }, // end props
  data: function() {
    return {
      indexName: this.lang == "fr" ? process.env.VUE_APP_ALGOLIA_LIST_FR : process.env.VUE_APP_ALGOLIA_LIST_EN,
      keyword: null,
      algoliaResults: null,
      naicsResults:[],
      nomatch: false
    };
  }, // end data
  mounted: function() {
    this.$i18n.locale = this.mx_toggleLang;
  }, // end mounted
  watch: {
    clearkeyword: function() {
      this.keyword = "";
    },
    lang: function() {
      this.$i18n.locale = this.mx_toggleLang;
      this.indexName = this.lang == "fr" ? process.env.VUE_APP_ALGOLIA_LIST_FR : process.env.VUE_APP_ALGOLIA_LIST_EN;
    } // end lang
  }, // end watch
  methods: {
    onSearch: function(event) {
      this.updateShowDetail(false);
      this.updateSearchKeyword(event);
      if (event.length > 0) {
        let newTreeNode = [];
        getConnexion(this.indexName, event.trim())
          .then((hits) => {
            this.algoliaResults = hits;
            this.updateAlgoliaHitsResult(hits);

            if (hits.length > 0) {
              let naicsFromAlgolia = hits.map(hit => { return `${hit.naics}`});
              naicsFromAlgolia = [...new Set(naicsFromAlgolia)]; // to get only unique element
              
              // count for matches text
              this.naicsResults = naicsFromAlgolia;

              // merge ID if checked exist
              let allChecked = this.getAllCheckedValues;
              if (allChecked["naics"].length != undefined) {
                let checkedID = allChecked["naics"].map(item => { return `${item.code}`});
                checkedID = [...new Set(checkedID)]; // to get only unique element
                naicsFromAlgolia = naicsFromAlgolia.concat(checkedID);
                naicsFromAlgolia = [...new Set(naicsFromAlgolia)]; // to get only unique element                    
              }
              // do tree from results
              newTreeNode = JSON.parse(JSON.stringify(this.getInitTree)); // to really make a copy not only reference

              newTreeNode.forEach((topNaicsObject, index) => {
                this.traverseTree(topNaicsObject, naicsFromAlgolia);
              }); // end foreach

              newTreeNode = newTreeNode.filter(obj => !(obj && Object.keys(obj).length === 0)); // to remove empty array
              newTreeNode.forEach(node => {
                this.removeEmptyObjectsInTree(node);
              }); // end foreach
              this.updateHasResults(true);
              this.nomatch = false;
            } else {
              this.nomatch = true; 
              this.updateHasResults(false);

              this.$gtag.event("industry-not-found", {
                event_category: "naics-tree-widget",
                event_label: this.keyword,
                product: process.env.VUE_APP_PRODUCT
              });
            }
            // send back results
            this.updateSearchKeyword(this.keyword);
            this.$emit("update", newTreeNode);
          })
          .catch(err => {
            console.log(err);
            this.$emit("errored", true);
          });
      }
      if (this.keyword.length == 0) {
        this.updateHasResults(false);
        this.updateAlgoliaHitsResult([]);
        let allChecked = this.getAllCheckedValues; 
        let allSaved = this.getAllSavedValues;
        if (allChecked["naics"].length == undefined || allChecked["naics"].length == 0 ) {
          this.$emit("update", this.getInitTree);
        } else if (allSaved["naics"].length == allChecked["naics"].length) {
          this.$emit("update", this.getSavedTree);
        }
      }
    }, // end onSearch
    isNaicsLevelInAlgoliaReturn: function(naics, fromAlgolia) {
      let inAlogliaReturn = false; 
      if (Array.isArray(fromAlgolia)) {
        fromAlgolia.forEach(naicsAlg => { 
          // because now structure can be 31-33 for top level
          let arrayNaics = naics.split("-");
          let start = parseInt(arrayNaics[0])
          let end = parseInt(arrayNaics[arrayNaics.length-1]);
          for (let i=start; i <= end ;i++) {
            var regex = new RegExp("\\b"+i.toString(),"g");
            let n = naicsAlg.search(regex);
            if (n > -1 && !inAlogliaReturn) {
              inAlogliaReturn = true;
            }  
          } // end for
        });
      }
      return inAlogliaReturn;
    }, // end isNaicsLevelInAlgoliaReturn
    traverseTree: function(node, naicsFromAlgolia) {
      if (!this.isNaicsLevelInAlgoliaReturn(node.id, naicsFromAlgolia)) {
        Object.keys(node).forEach(key => {
          delete node[key];
        }); // end foreach
      }
      if (node.children) {
        node.children.forEach(childNode => {
          this.traverseTree(childNode,naicsFromAlgolia);
        }); // end foreach
      } // end if children
    }, // end traverseTree
    removeEmptyObjectsInTree: function(node) {
      if (node.children) {
        // remove empty obj if exist
        if (Array.isArray(node.children)) {
          node.children = node.children.filter(obj => !(obj && Object.keys(obj).length === 0)); // to remove empty array/object
        }
        node.children.forEach(childNode => {
          this.removeEmptyObjectsInTree(childNode);
        }); // end foreach
      } // end if children
    } // end removeEmptyObjectsInTree
  } // end methods
} // end export default
</script>

<style>
.open-services-tree-naics-lookup label.keyword-label {
  float: left;
  color: #fff;
}
.open-services-tree-naics-lookup .noresults {
  font-size: small; 
  color: rgba(255,255,255,0.9);
  float: left;
  padding: 0.75em 2em 
}
.open-services-tree-naics-lookup .results {
  font-size: small; 
  color: rgba(255,255,255,0.9);
  float: left;
  padding: 0.75em 2em 
}
.open-services-tree-naics-lookup .keyword-field {
  float: left;
  margin-bottom: 0px;
  width: 90%;
  clear: left;
  margin-top: 12px;
}
.open-services-tree-naics-lookup .control .input:focus {
  border-color: #38598a !important;
  box-shadow: none;
}
@media only screen and (max-width: 767px) {
  .open-services-tree-naics-lookup .keyword-field {
    margin-left: 15px;
  }
  .open-services-tree-naics-lookup label.keyword-label {
    color: #000;
    margin-left: 15px;
    margin-top: 15px;
  }
  .open-services-tree-naics-lookup .noresults {
    color: #000;
  }
  .open-services-tree-naics-lookup .results {
    color: #000;
  }
}
</style>
